import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Browser Supabase client for the Auth module (graduated out of src/dev when
 * the real login screen landed). The URL + anon key are injected at build time:
 * in dev they come from the gitignored apps/web/.env.local (Vite auto-loads
 * it), in production CI passes repo-level VITE_SUPABASE_URL /
 * VITE_SUPABASE_ANON_KEY into the Vite build (.github/workflows/ci.yml
 * deploy-web). The anon key is a public, RLS-guarded key — baking it into the
 * bundle is the standard Supabase browser setup, not a secret leak.
 *
 * Lazily constructed so importing this module never throws at bundle-eval
 * time — a missing config surfaces as a caught, user-visible auth error
 * instead of a blank screen.
 */
let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (client) return client;

  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const url = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error('Supabase client is missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY');
  }

  client = createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return client;
}
